/**
 * PlaceDirectionsView — presentational directions/map branch of PlaceDetailModule.
 * The maps-embed query lives in the parent; its resolved embed URL is threaded
 * down as `mapsEmbedUrl`.
 */

interface PlaceDirectionsViewProps {
  mapsEmbedUrl: string | undefined;
  address: string;
  destination: string;
  placeTitle: string;
  onBack: () => void;
  isMobile: boolean;
}

export function PlaceDirectionsView({
  mapsEmbedUrl,
  address,
  destination,
  placeTitle,
  onBack,
  isMobile,
}: PlaceDirectionsViewProps) {
  return (
    <div
      style={{
        height: isMobile ? "100dvh" : "75vh",
        width: isMobile ? "100vw" : "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        position: isMobile ? "fixed" : "relative",
        top: isMobile ? 0 : "auto",
        left: isMobile ? 0 : "auto",
        right: isMobile ? 0 : "auto",
        bottom: isMobile ? 0 : "auto",
        background: "var(--color-surface)",
        borderRadius: isMobile ? "0" : "12px",
        zIndex: isMobile ? 10000 : "auto",
      }}
    >
      <div
        style={{
          padding: "16px",
          borderBottom: "1px solid var(--color-border)",
          display: "flex",
          alignItems: "center",
          gap: "12px",
        }}
      >
        <button
          onClick={onBack}
          style={{
            background: "var(--color-surface)",
            border: "1px solid var(--color-border)",
            borderRadius: "50%",
            width: "32px",
            height: "32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
          }}
          title="Back"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
        </button>
        <div>
          <h2 style={{ fontSize: "18px", fontWeight: "600", margin: 0 }}>
            {placeTitle}
          </h2>
          {address && (
            <p
              style={{
                fontSize: "14px",
                color: "var(--color-text-muted)",
                margin: "2px 0 0 0",
              }}
            >
              {address}
            </p>
          )}
        </div>
      </div>

      <div style={{ flex: 1, position: "relative" }}>
        {mapsEmbedUrl ? (
          <iframe
            src={mapsEmbedUrl}
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            title={`Directions to ${placeTitle}`}
          />
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: "var(--color-text-faint)",
            }}
          >
            Loading map...
          </div>
        )}
      </div>

      <div
        style={{
          padding: "16px",
          borderTop: "1px solid var(--color-border)",
          background: "var(--color-surface-muted)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <p style={{ fontSize: "14px", color: "var(--color-text-muted)" }}>
          Showing directions from your current location
        </p>
        <a
          href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            padding: "8px 16px",
            background: "var(--color-primary)",
            color: "white",
            borderRadius: "8px",
            fontSize: "14px",
            fontWeight: "500",
            textDecoration: "none",
            transition: "background 0.2s",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.background = "var(--color-primary-hover)")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.background = "var(--color-primary)")
          }
        >
          Open in Google Maps
        </a>
      </div>
    </div>
  );
}
